import { useCallback, useState } from 'react';
import { BaseListFilterSelectProps, ListFilterOption, usePrepareOptions } from './common';
import ListFilterSelect from './ListFilterSelect';
import { AnalyticsTriggerElementProps } from '@common/types';
import { Box, Stack } from '@mui/material';
import ListFilterSelectTitle from './ListFilterSelectTitle';
import ListFilterSelectSearch from './ListFilterSelectSearch';

interface ListFilterMultiSelectProps extends BaseListFilterSelectProps {
  value?: ListFilterOption[];
  onSelect: (value: ListFilterOption[]) => void;
  disabledSearch?: boolean;
  width?: number;
}

export default function ListFilterMultiSelect({
  value,
  onSelect,
  options,
  title,
  enableSearch = false,
  onSearch,
  searchValue,
  searchPlaceholder,
  maxHeight = 300,
  width,
  groupedByCategory,
  isFetching,
  disabledSearch,
  selectedFirst,
  ...otherProps
}: ListFilterMultiSelectProps & AnalyticsTriggerElementProps) {
  const [localSearchValue, setLocalSearchValue] = useState('');

  const handleSelect = useCallback(
    (option: ListFilterOption) => {
      const selectedValues = new Set(value?.map((v) => v.value));
      if (selectedValues.has(option.value)) {
        onSelect(value?.filter((v) => v.value !== option.value) ?? []);
      } else {
        onSelect([...(value ?? []), option]);
      }
    },
    [value, onSelect],
  );

  const items = usePrepareOptions({
    options,
    handler: handleSelect,
    enableSearch,
    searchValue: localSearchValue,
    type: 'checkbox',
    selectedValues: value ? new Set(value?.map((v) => v.value)) : undefined,
    groupedByCategory,
    selectedFirst,
  });

  const showSearch = enableSearch || !!onSearch;
  const handleOnSearch = (newSearchValue: string) => {
    if (onSearch) {
      return onSearch(newSearchValue);
    }

    setLocalSearchValue(newSearchValue);
  };

  return (
    <Box width={width}>
      <Stack direction="column" justifyContent="center" alignItems="stretch">
        {title && <ListFilterSelectTitle title={title} />}
        {showSearch && (
          <ListFilterSelectSearch
            value={searchValue || localSearchValue}
            searchPlaceholder={searchPlaceholder}
            onSearch={handleOnSearch}
            disabled={disabledSearch}
          />
        )}
        <ListFilterSelect maxHeight={maxHeight} items={items} isFetching={isFetching} {...otherProps} />
      </Stack>
    </Box>
  );
}
